import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import useTranslation from "next-translate/useTranslation";
import { useUser } from "@/redux/user";
import {
  pendingGuestTransfer,
  canOfferGuestTransfer,
  queueGuestTransfer,
  retryGuestTransfer,
  dismissGuestTransfer,
} from "./guestProgressTransfer";

/** Retry the account-bound snapshot; ask before claiming unbound history on a shared device. */
export default function GuestProgressTransferNotice() {
  const { id, memberLoggedIn } = useUser();
  const { pathname } = useRouter();
  const { t } = useTranslation("common");
  const [revision, update] = useState(0);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    setSaved(false);
    if (!memberLoggedIn || !id) return;
    let cancelled = false;
    const retry = () => {
      if (!pendingGuestTransfer(id)) {
        update((n) => n + 1);
        return;
      }
      setBusy(true);
      void retryGuestTransfer(id).then((ok) => {
        if (!cancelled) {
          setBusy(false);
          if (ok) setSaved(true);
          update((n) => n + 1);
        }
      });
    };
    retry();
    const changed = () => update((n) => n + 1);
    window.addEventListener("online", retry);
    window.addEventListener("focus", retry);
    window.addEventListener("chess-activity", changed);
    window.addEventListener("storage", changed);
    return () => {
      cancelled = true;
      window.removeEventListener("online", retry);
      window.removeEventListener("focus", retry);
      window.removeEventListener("chess-activity", changed);
      window.removeEventListener("storage", changed);
    };
  }, [id, memberLoggedIn]);
  void revision;
  if (!memberLoggedIn || !id || !pathname.startsWith("/learn")) return null;
  const pending = pendingGuestTransfer(id),
    offer = canOfferGuestTransfer(id);
  if (!pending && !offer && !saved) return null;
  const transfer = async () => {
    setBusy(true);
    try {
      if (queueGuestTransfer(id)) setSaved(await retryGuestTransfer(id));
    } finally {
      setBusy(false);
      update((n) => n + 1);
    }
  };
  return (
    <aside
      className="mx-auto mt-3 flex max-w-6xl flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-athens-gray-200 bg-white px-4 py-3 text-sm"
      aria-live="polite"
    >
      <p className="w-full min-w-0 sm:w-auto sm:flex-1">
        {t(
          saved
            ? "guestProgress.saved"
            : pending
              ? "guestProgress.retryDescription"
              : "guestProgress.claimDescription",
        )}
      </p>
      {!saved && (
        <button
          type="button"
          className="min-h-[44px] text-altblue-600 hover:underline"
          disabled={busy}
          onClick={() => void transfer()}
        >
          {t(
            busy
              ? "guestProgress.saving"
              : pending
                ? "Try again"
                : "guestProgress.add",
          )}
        </button>
      )}
      {!busy && (
        <button
          type="button"
          className="min-h-[44px] text-slate-500 hover:underline"
          onClick={() => {
            setSaved(false);
            dismissGuestTransfer(id);
            update((n) => n + 1);
          }}
        >
          {t(saved ? "guestProgress.close" : "guestProgress.keepSeparate")}
        </button>
      )}
    </aside>
  );
}
